/**
 * Tab "Advance" (NPE) của màn Contract Detail — mirror Access
 * `frmProcurementQryDetailsNPETab2` (toolbar New/Copy/Edit/Delete/Print) +
 * `…Tab2_Advanced` (datasheet `ProcurementPayQryAdvanced_NPE`, TabIndex order).
 *
 * Cột "Status" (must_fix_txt) đỏ khi "To be fix" (Access ConditionalFormat).
 * Toolbar thao tác trên current record (row select) — Access Me.subForm.Form.paydata_p_id.
 */
import { Button } from '@mui/material'
import { useMemo, useState } from 'react'

import { useListContractAdvanceQuery, type AdvanceRow } from '../../store/api/procurementApi'
import ProcurementDatasheet, {
  type DatasheetColumn,
  type DatasheetRow,
} from './ProcurementDatasheet'

// Thứ tự cột theo Access TabIndex (form …Tab2_Advanced); không có ColumnOrder trong CSV.
// sum:true = Access TotalsRow AggregateType=Sum trên cột tiền/quantity (KHÔNG sum price/rate/fx).
// freeze = 1 cột định danh (Advanced ID). CF Status "To be fix" → đỏ (Access ConditionalFormat).
const ADVANCE_COLUMNS: DatasheetColumn[] = [
  { field: 'paydata_p_id', header: 'ID', kind: 'id', width: 88, freeze: true },
  { field: 'payment_date', header: 'Advanced Date', kind: 'date', width: 108 },
  { field: 'contract_no', header: 'Contract No', kind: 'text', width: 100 },
  { field: 'xntt_no', header: 'XNTT No', kind: 'text', width: 108 },
  { field: 'payment_type', header: 'Payment Type', kind: 'text', width: 100, filter: 'checkboxes' },
  { field: 'bank', header: 'Request - Bank', kind: 'text', width: 104, filter: 'checkboxes' },
  { field: 'quantity', header: 'Request - Quantity', kind: 'int', width: 116, sum: true },
  { field: 'price', header: 'Request - Price', kind: 'int', width: 104 },
  { field: 'requested_usd', header: 'Requested USD', kind: 'int', width: 108, sum: true },
  { field: 'requested_fx', header: 'Requested FX', kind: 'num2', width: 100 },
  { field: 'requested_vnd', header: 'Requested VND', kind: 'int', width: 112, sum: true },
  { field: 'vat_amount_vnd', header: 'VAT Amount VND', kind: 'int', width: 114, sum: true },
  { field: 'paid_usd', header: 'Actual Payment - USD', kind: 'int', width: 130, sum: true },
  { field: 'fx', header: 'Actual Payment - FX', kind: 'int', width: 122 },
  { field: 'paid_vnd', header: 'Actual Payment - VND', kind: 'int', width: 132, sum: true },
  { field: 'reconcile', header: 'Reconcile', kind: 'num2', width: 96 },
  { field: 'interest_rate', header: 'NPEs - Interest Rate', kind: 'percent', width: 122 },
  { field: 'fixation_deadline', header: 'NPEs - Fixation Deadline', kind: 'date', width: 140 },
  { field: 'invoice_no', header: 'Invoice No', kind: 'text', width: 110 },
  { field: 'invoice_date', header: 'Invoice Date', kind: 'date', width: 100 },
  { field: 'invoice_amount', header: 'Invoice Amount', kind: 'int', width: 112 },
  { field: 'warehouse', header: 'Others - Warehouse', kind: 'text', width: 130, filter: 'checkboxes' },
  { field: 'note', header: 'Others - Note', kind: 'text', width: 120 },
  { field: 'supplier_id', header: 'BankInfo - Supplier ID', kind: 'text', width: 130 },
  { field: 'bank_account', header: 'BankAcc Number', kind: 'text', width: 124 },
  { field: 'account_name', header: 'BankAcc Name', kind: 'text', width: 150 },
  { field: 'bank_name', header: 'Bank Name', kind: 'text', width: 150 },
  { field: 'contract_date', header: 'Other - Contract Date', kind: 'date', width: 128 },
  { field: 'quality_code', header: 'Other - Quality', kind: 'text', width: 104, filter: 'checkboxes' },
  { field: 'date_update', header: 'Updated Date', kind: 'datetime', width: 110 },
  { field: 'person_update', header: 'Updated Person', kind: 'text', width: 120 },
  {
    field: 'must_fix_txt',
    header: 'Status',
    kind: 'text',
    width: 90,
    filter: 'checkboxes',
    redWhen: (v) => String(v ?? '').trim().toLowerCase() === 'to be fix',
  },
]

const toolbarBtnSx = {
  minWidth: 0,
  px: 1,
  py: 0.2,
  minHeight: 26,
  textTransform: 'none' as const,
  fontSize: '0.72rem',
  lineHeight: 1.1,
}

interface Props {
  contractNo: string
  canEdit: boolean
  onNew: () => void
  onCopy: (row: AdvanceRow) => void
  onEdit: (row: AdvanceRow) => void
  onDelete: (row: AdvanceRow) => void
  onPrint: (row: AdvanceRow) => void
}

export default function ContractDetailAdvance({
  contractNo,
  canEdit,
  onNew,
  onCopy,
  onEdit,
  onDelete,
  onPrint,
}: Props) {
  const advance = useListContractAdvanceQuery({ contractNo })
  const [selectedId, setSelectedId] = useState<string | null>(null)

  const rows = useMemo<DatasheetRow[]>(
    () =>
      (advance.data?.items ?? []).map((it, idx) => ({
        ...(it as Record<string, unknown>),
        id: `${it.paydata_p_id ?? idx}`,
      })),
    [advance.data?.items],
  )

  const selectedRow = useMemo(
    () => (advance.data?.items ?? []).find((it) => `${it.paydata_p_id}` === selectedId) ?? null,
    [advance.data?.items, selectedId],
  )

  const needRow = (fn: (row: AdvanceRow) => void) => () => {
    if (selectedRow) fn(selectedRow)
  }

  return (
    <ProcurementDatasheet
      columns={ADVANCE_COLUMNS}
      storageKey="procurement.contract-detail.advance"
      rows={rows}
      isLoading={advance.isLoading}
      isFetching={advance.isFetching}
      error={advance.error}
      primaryField="paydata_p_id"
      searchPlaceholder="Search advances…"
      errorTitle="Could not load advances"
      emptyTitle="No advances"
      emptyDescription="No rows for the selected contract."
      selectedRowId={selectedId}
      onRowSelect={(row) => setSelectedId(row ? row.id : null)}
      rightSlot={
        <>
          <Button size="small" variant="contained" color="primary" sx={toolbarBtnSx} disabled={!canEdit} onClick={onNew}>
            New
          </Button>
          <Button size="small" variant="outlined" color="primary" sx={toolbarBtnSx} disabled={!canEdit || !selectedRow} onClick={needRow(onCopy)}>
            Copy
          </Button>
          <Button size="small" variant="outlined" color="primary" sx={toolbarBtnSx} disabled={!canEdit || !selectedRow} onClick={needRow(onEdit)}>
            Edit
          </Button>
          <Button size="small" variant="outlined" color="error" sx={toolbarBtnSx} disabled={!canEdit || !selectedRow} onClick={needRow(onDelete)}>
            Delete
          </Button>
          <Button size="small" variant="outlined" color="primary" sx={toolbarBtnSx} disabled={!selectedRow} onClick={needRow(onPrint)} title="Pr-XNTT">
            Pr-XNTT
          </Button>
        </>
      }
    />
  )
}
